import { createHeadersFormOptions } from "../dataProvider/fetch";
import { checkAppVersion } from "../dataProvider/utils";
import { handleAuthStatus } from "./authSession";
import { buildApiUrl, extractErrorMessage } from "../dataProvider/requestJson";

/**
 * Rule builder admin API (`/restapi/admin/rules/**`, system administrators only, reads included).
 * Contract: docs/drafts/rule-builder-frontend.md §8.
 *
 * Failures carry `error.status` and, for JSON bodies, `error.details` (the parsed body), because
 * the 409s of this API carry data the UI acts on: `conflictingSetId`, `existingRuleId`,
 * `affectedSets`, `eventCount`, `usageCount`.
 */
const BASE = "admin/rules";

/** The admin UI is English-only; rule text and labels are requested in this locale. */
export const RULES_LOCALE = "en-US";

const STATUS_MESSAGES = {
  401: "Your session has expired. Log in again.",
  403: "Only system administrators can maintain rules.",
};

export const buildQuery = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    usp.append(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

const parseJson = (text) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
};

const request = async (path, { method = "GET", body } = {}) => {
  const options = { method };
  if (body !== undefined) options.body = JSON.stringify(body);
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: createHeadersFormOptions(options),
  });
  const text = await response.text();
  checkAppVersion(response.headers.get("x-version"));
  handleAuthStatus(response.status);
  if (!response.ok) {
    const error = new Error(
      extractErrorMessage(text, response.status, STATUS_MESSAGES),
    );
    error.status = response.status;
    const details = parseJson(text);
    error.details = details && typeof details === "object" ? details : null;
    throw error;
  }
  if (!text) return null;
  const json = parseJson(text);
  return json === null ? text : json;
};

const enc = encodeURIComponent;
const withLocale = (params) => ({ locale: RULES_LOCALE, ...(params || {}) });

export const RULE_PATHS = {
  types: () => `${BASE}/types${buildQuery(withLocale())}`,
  categories: () => `${BASE}/categories${buildQuery(withLocale())}`,
  heights: () => `${BASE}/heights${buildQuery(withLocale())}`,
  fullCodeModes: () => `${BASE}/full-code-modes${buildQuery(withLocale())}`,

  preview: () => `${BASE}/preview${buildQuery(withLocale())}`,
  rules: (params) => `${BASE}${buildQuery(withLocale(params))}`,
  rule: (id) => `${BASE}/${enc(id)}${buildQuery(withLocale())}`,
  ruleUsage: (id) => `${BASE}/${enc(id)}/usage`,
  ruleDelete: (id) => `${BASE}/${enc(id)}`,

  groups: (params) => `${BASE}/groups${buildQuery(withLocale(params))}`,
  group: (id) => `${BASE}/groups/${enc(id)}${buildQuery(withLocale())}`,
  groupCreate: () => `${BASE}/groups${buildQuery(withLocale())}`,

  setsInGroup: (groupId, params) =>
    `${BASE}/groups/${enc(groupId)}/sets${buildQuery(withLocale(params))}`,
  sets: (params) => `${BASE}/sets${buildQuery(withLocale(params))}`,
  set: (id) => `${BASE}/sets/${enc(id)}${buildQuery(withLocale())}`,
  setCreate: () => `${BASE}/sets${buildQuery(withLocale())}`,
  setCopy: (id, targetGroupId) =>
    `${BASE}/sets/${enc(id)}/copy${buildQuery(withLocale({ targetGroupId }))}`,
  setStatus: (id) =>
    `${BASE}/sets/${enc(id)}/status${buildQuery(withLocale())}`,
  setDelete: (id) => `${BASE}/sets/${enc(id)}`,
};

/**
 * The list filter "Classification" maps onto the three exact-match booleans: Standard is all
 * three false; Novice / Synchro / Masters each pin one flag to true and leave the others open.
 */
export const classificationParams = (classification) => {
  switch (classification) {
    case "STANDARD":
      return { novice: false, synchro: false, masters: false };
    case "NOVICE":
      return { novice: true };
    case "SYNCHRO":
      return { synchro: true };
    case "MASTERS":
      return { masters: true };
    default:
      return {};
  }
};

export const rulesApi = {
  // ---- catalog & lookups ----
  types: () => request(RULE_PATHS.types()),
  categories: () => request(RULE_PATHS.categories()),
  heights: () => request(RULE_PATHS.heights()),
  fullCodeModes: () => request(RULE_PATHS.fullCodeModes()),

  // ---- rules ----
  /** `{ text, valid, errors: [{field, message}], existingRuleId, existingUsageCount }` */
  preview: (draft) =>
    request(RULE_PATHS.preview(), { method: "POST", body: draft }),
  /** Find-or-create: the DTO's `created` says which. */
  findOrCreateRule: (draft) =>
    request(RULE_PATHS.rules(), { method: "POST", body: draft }),
  rules: ({
    page,
    size,
    sort,
    isAscending,
    type,
    category,
    query,
    unusedOnly,
  } = {}) =>
    request(
      RULE_PATHS.rules({
        page,
        size,
        sort,
        isAscending,
        type,
        category,
        query,
        unusedOnly,
      }),
    ),
  rule: (id) => request(RULE_PATHS.rule(id)),
  ruleUsage: (id) => request(RULE_PATHS.ruleUsage(id)),
  /** `draft + { applyToAllSets }`; 409 details carry `affectedSets` or `existingRuleId`. */
  updateRule: (id, body) =>
    request(RULE_PATHS.rule(id), { method: "PUT", body }),
  deleteRule: (id) => request(RULE_PATHS.ruleDelete(id), { method: "DELETE" }),

  // ---- rule groups ----
  groups: ({
    page,
    size,
    sort,
    isAscending,
    status,
    governingBodyId,
    query,
  } = {}) =>
    request(
      RULE_PATHS.groups({
        page,
        size,
        sort,
        isAscending,
        status,
        governingBodyId,
        query,
      }),
    ),
  group: (id) => request(RULE_PATHS.group(id)),
  /** `{ name, status, governingBodyIds }` */
  createGroup: (body) =>
    request(RULE_PATHS.groupCreate(), { method: "POST", body }),
  updateGroup: (id, body) =>
    request(RULE_PATHS.group(id), { method: "PUT", body }),

  // ---- rule sets ----
  setsInGroup: (groupId, params) =>
    request(RULE_PATHS.setsInGroup(groupId, params)),
  sets: (params) => request(RULE_PATHS.sets(params)),
  set: (id) => request(RULE_PATHS.set(id)),
  /** Upsert body: docs/drafts/rule-builder-frontend.md §8.6. */
  createSet: (body) =>
    request(RULE_PATHS.setCreate(), { method: "POST", body }),
  updateSet: (id, body) => request(RULE_PATHS.set(id), { method: "PUT", body }),
  copySet: (id, targetGroupId) =>
    request(RULE_PATHS.setCopy(id, targetGroupId), {
      method: "POST",
      body: {},
    }),
  setStatus: (id, status) =>
    request(RULE_PATHS.setStatus(id), { method: "PATCH", body: { status } }),
  deleteSet: (id) => request(RULE_PATHS.setDelete(id), { method: "DELETE" }),
};

// ---- display helpers shared by the rule screens ------------------------------------------

export const RULE_CATEGORY_ORDER = [
  "COUNT_STRUCTURE",
  "DIVE_SELECTION",
  "CATEGORIES",
  "DIFFICULTY",
  "HEIGHTS",
];

export const RULE_CATEGORY_LABELS = {
  COUNT_STRUCTURE: "Count and structure",
  DIVE_SELECTION: "Dive selection",
  CATEGORIES: "Categories",
  DIFFICULTY: "Degree of difficulty",
  HEIGHTS: "Heights",
};

export const ruleCategoryLabel = (key) =>
  RULE_CATEGORY_LABELS[key] || key || "";

/** Header `height` of a rule set (text column, CHECK-constrained). */
export const SET_HEIGHT_OPTIONS = [
  { value: "1", label: "1 m" },
  { value: "3", label: "3 m" },
  { value: "10", label: "Platform" },
  { value: "27", label: "High diving" },
  { value: "Any", label: "Any" },
];

export const setHeightLabel = (value) =>
  SET_HEIGHT_OPTIONS.find((o) => o.value === String(value))?.label ||
  String(value ?? "");

export const GENDER_OPTIONS = ["Male", "Female", "Any"];

export const CLASSIFICATION_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "NOVICE", label: "Novice" },
  { value: "SYNCHRO", label: "Synchro" },
  { value: "MASTERS", label: "Masters" },
];

export const classificationLabel = ({ novice, synchro, masters } = {}) => {
  const on = [];
  if (novice) on.push("Novice");
  if (synchro) on.push("Synchro");
  if (masters) on.push("Masters");
  return on.length ? on.join(" + ") : "Standard";
};

/**
 * A rule set's jump schema is the dive count, then the default voluntary / optional order as
 * blocks: "11-2v-3o-2v-1o-1v-2o". The editor works on one letter per round ("vvooovvovoo").
 * The order only pre-fills the dive sheet; rules restrict what a diver may change.
 */
export const schemaRounds = (schema) => {
  const s = String(schema || "")
    .trim()
    .toLowerCase();
  if (!/^[0-9]{1,2}(-[0-9]{1,2}[vo])+$/.test(s)) return null;
  const [count, ...blocks] = s.split("-");
  const rounds = blocks
    .map((b) => b.slice(-1).repeat(Number(b.slice(0, -1))))
    .join("");
  return rounds.length === Number(count) ? rounds : null;
};

/** Mirrors the server: voluntaries-then-optionals stays "N-Vv-Oo", anything else is blocks. */
export const schemaFromRounds = (rounds) => {
  const counts = roundCounts(rounds);
  if (/^v*o*$/.test(rounds)) {
    return `${rounds.length}-${counts.voluntaries}v-${counts.optionals}o`;
  }
  const blocks = rounds.match(/v+|o+/g) || [];
  return [rounds.length, ...blocks.map((b) => `${b.length}${b[0]}`)].join("-");
};

/** The stored spelling while the order is unchanged (e.g. "6-6o"), else the server's form. */
export const schemaFor = (rounds, storedSchema) =>
  storedSchema && schemaRounds(storedSchema) === rounds
    ? storedSchema
    : schemaFromRounds(rounds);

export const roundCounts = (rounds) => {
  const voluntaries = (String(rounds).match(/v/g) || []).length;
  return { voluntaries, optionals: String(rounds).length - voluntaries };
};

/** "2026-08-02T10:15:30Z" -> "2026-08-02 10:15" (local time). */
export const formatInstant = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Last change as `{ at: "2026-08-02 10:15", by: "rmannion" | null }`, or null when seeded. */
export const auditParts = (audit) => {
  const at = audit?.updatedAt || audit?.createdAt;
  if (!at) return null;
  const by = audit.updatedAt
    ? audit.updatedByName || audit.updatedBy
    : audit.createdByName || audit.createdBy;
  return { at: formatInstant(at), by: by ? String(by) : null };
};

/** "2026-08-02 10:15 · rmannion", or "migration" for rows seeded without audit data. */
export const auditLine = (audit) => {
  const parts = auditParts(audit);
  if (!parts) return "migration";
  return [parts.at, parts.by].filter(Boolean).join(" · ");
};

export const auditSentence = (audit) => {
  if (!audit || (!audit.createdAt && !audit.updatedAt)) {
    return "Seeded by a migration";
  }
  const parts = [];
  if (audit.createdAt) {
    parts.push(
      `Created ${formatInstant(audit.createdAt)}${
        audit.createdByName ? ` by ${audit.createdByName}` : ""
      }`,
    );
  }
  if (audit.updatedAt) {
    parts.push(
      `Updated ${formatInstant(audit.updatedAt)}${
        audit.updatedByName ? ` by ${audit.updatedByName}` : ""
      }`,
    );
  }
  return parts.join(" · ");
};
